import React, { useEffect, useState } from "react";
import Axios from "axios";
import { API_URL } from "./api";
import "./SubscriptionPing.css";

const formatStatus = (status) =>
  (status || "error")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

function SubscriptionPing({ subscriptionId }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    const runPing = async () => {
      setLoading(true);
      setResult(null);

      if (!subscriptionId) {
        setResult({
          status: "invalid_request",
          message: "No subscription was selected.",
        });
        setLoading(false);
        return;
      }

      let accessToken = null;

      try {
        accessToken = JSON.parse(
          localStorage.getItem("authTokens")
        )?.access;
      } catch (error) {
        accessToken = null;
      }

      if (!accessToken) {
        setResult({
          status: "authentication_required",
          message: "Your session expired. Please sign in again.",
        });
        setLoading(false);
        return;
      }

      try {
        const response = await Axios.post(
          `${API_URL}/radius/ping/`,
          { subscription_id: subscriptionId },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            signal: controller.signal,
            timeout: 15000,
          }
        );

        if (!controller.signal.aborted) {
          setResult(response.data);
        }
      } catch (error) {
        if (
          controller.signal.aborted ||
          error.code === "ERR_CANCELED"
        ) {
          return;
        }

        const responseData = error.response?.data;

        setResult(
          responseData && typeof responseData === "object"
            ? responseData
            : {
                status: "error",
                message:
                  error.code === "ECONNABORTED"
                    ? "The Ping request timed out."
                    : "The Ping request failed.",
              }
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    runPing();

    return () => controller.abort();
  }, [subscriptionId]);

  if (loading) {
    return (
      <div className="subscription-ping subscription-ping--loading p-3">
        Testing subscriber connectivity...
      </div>
    );
  }

  const status = result?.status || "error";
  const statusClass =
    status === "success"
      ? "text-success"
      : status === "unstable"
      ? "text-warning"
      : "text-danger";

  const hasMetrics =
    typeof result?.sent === "number" &&
    typeof result?.received === "number";

  return (
    <div className="testi subscription-ping p-3">
      <h4 className={statusClass}>
        Status: {formatStatus(status)}
      </h4>

      {result?.message && <p>{result.message}</p>}

      {hasMetrics && (
        <>
          <p>
            Replies: {result.received}/{result.sent}
          </p>
          <p>
            Packet loss: {result.packet_loss_percent}%
          </p>
          <p>
            Minimum:{" "}
            {result.minimum_ms == null
              ? "N/A"
              : `${result.minimum_ms} ms`}
          </p>
          <p>
            Average:{" "}
            {result.average_ms == null
              ? "N/A"
              : `${result.average_ms} ms`}
          </p>
          <p>
            Maximum:{" "}
            {result.maximum_ms == null
              ? "N/A"
              : `${result.maximum_ms} ms`}
          </p>

          <h5>Reply details</h5>

          {result.details_ms?.length > 0 ? (
            <ul>
              {result.details_ms.map((value, index) => (
                <li key={index}>{value} ms</li>
              ))}
            </ul>
          ) : (
            <p>No replies received.</p>
          )}
        </>
      )}
    </div>
  );
}

export default SubscriptionPing;
